// pages/OrderDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import CardAdmin from '../UI/CardAdmin';
import AssignManagerSelect from './AssignManagerSelect';
import StepList from './StepList';
import actionLabels from '../../config/actionLabels';
import Button from '../UI/Button';
import QRCodeBox from '../UI/QRCodeBox';
import ProgressBar from '../UI/ProgressBar';

const OrderDetailPage = () => {
    const { id } = useParams();

    const { user, logout, hasAnyRole, hasRole } = useUser();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [phaseManagers, setPhaseManagers] = useState({});
    const [phaseDepartments, setPhaseDepartments] = useState({});
    const navigate = useNavigate();
    const canViewOrchestration = hasAnyRole(["director", "departmentHead", "chief", "unitChief"]);
    const canViewDispatching = hasAnyRole([ "masrter", "teamLead", ]);
    const canViewCompletion = hasRole("worker");

    const fetchOrder = async () => {
		console.log("fetchOrder")
        try {
            const res = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/orders/${id}`, {}, logout);
            const data = await res.json();
            setOrder(data.order);
        } catch (err) {
            setError('Ошибка загрузки заказа');
        } finally {
            setLoading(false);
        }
    };

    const fetchPhaseManager = async (phaseIndex) => {
        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${id}/phase/${phaseIndex}/manager`,
                {},
                logout
            );
            const data = await res.json();
            setPhaseManagers(prev => ({ ...prev, [phaseIndex]: data.manager }));
        } catch (err) {
            console.error('Ошибка загрузки информации о руководителе:', err);
        }
    };

    const fetchPhaseDepartment = async (phaseIndex) => {
        try {
            const res = await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${id}/phase/${phaseIndex}/department`,
                {},
                logout
            );
            const data = await res.json();
            setPhaseDepartments(prev => ({ ...prev, [phaseIndex]: data.department }));
        } catch (err) {
            console.error('Ошибка загрузки информации об отделе:', err);
        }
    };

    const handlePhaseStatusChange = async (phaseIndex, newStatus) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${id}/phase/${phaseIndex}/status`,
                {
                    method: 'POST',
                    body: JSON.stringify({ status: newStatus }),
                },
                logout
            );
            fetchOrder();
        } catch (err) {
            const errorMessage = err.message.replace('Ошибка в fetchWithAuth: Error: ', '');
            setError(errorMessage);
        }
    };

    const handleOrderStatusChange = async (newStatus) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${id}/status`,
                {
                    method: 'POST',
                    body: JSON.stringify({ status: newStatus }),
                },
                logout
            );
            fetchOrder();
        } catch (err) {
            const errorMessage = err.message.replace('Ошибка в fetchWithAuth: Error: ', '');
            setError(errorMessage);
        }
    };

    useEffect(() => {
        fetchOrder();
    }, [id]);

    useEffect(() => {
        if (order) {
            order.productSnapshot.route.forEach((_, index) => {
                fetchPhaseManager(index);
                fetchPhaseDepartment(index);
            });
        }
    }, [order]);

    const handleManagerAssign = async (phaseIndex, userId) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${id}/assign-manager`,
                {
                    method: 'POST',
                    body: JSON.stringify({ 
                        phaseIndex, 
                        userId: userId || null // Если userId пустой, значит снимаем назначение
                    }),
                },
                logout
            );
            fetchOrder();
        } catch (err) {
            setError('Ошибка назначения руководителя');
        }
    };

    const handleStepComplete = async (phaseIndex, stepIndex) => {
        try {
            await fetchWithAuth(
                `${process.env.REACT_APP_API_URL}/orders/${id}/complete-step`,
                {
                    method: 'POST',
                    body: JSON.stringify({ phaseIndex, stepIndex }),
                },
                logout
            );
            fetchOrder();
        } catch (err) {
            setError('Ошибка завершения шага');
        }
    };

    if (loading) return <div>Загрузка...</div>;
    if (error) return <div className="text-red-500">{error}</div>;
    if (!order) return <div>Заказ не найден</div>;

    return (
        <div className="space-y-6">
            <CardAdmin >
                <div className="flex justify-between items-center gap-4 mb-6">
                    <div>
                        <p className="text-gray-400">Заказ: {order.productSnapshot.name}</p>
                        <p className="text-gray-400">Вариация: {order.variation.value}</p>
                        <p className="text-gray-400">Количество: {order.quantity}</p>
                        <p className="text-gray-400">Срок выполнения: {new Date(order.deadline).toLocaleDateString()}</p>
                        <div className="mt-2">
                            <p className="text-gray-400">Прогресс выполнения:</p>
                            <ProgressBar progress={order.progress} />
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 rounded text-sm ${
                                    order.status === 'done' ? 'bg-green-500' :
                                    order.status === 'in_progress' ? 'bg-yellow-500' :
                                    'bg-gray-500'
                                }`}>
                                    {actionLabels.status[order.status]}
                                </span>
                                {canViewOrchestration && (
                                    <select
                                        value={order.status}
                                        onChange={(e) => handleOrderStatusChange(e.target.value)}
                                        className="bg-[#2D2D30] border border-[#3E3E42] rounded px-2 py-1 text-sm"
                                    >
                                        <option value="planned">Запланирован</option>
                                        <option value="in_progress">В работе</option>
                                        <option value="done">Завершен</option>
                                    </select>
                                )}
                            </div>
                            <p className="text-gray-400">Дата создания: {new Date(order.createdAt).toLocaleDateString()}</p>
                            <p className="text-gray-400">Приоритет: {actionLabels.priority[order.priority]}</p>
                        </div>
                        <div className="flex flex-col items-center">
                            <QRCodeBox
                                qrCodePath={order.qrCodePath}
                                type="order"
                                objectId={order._id}
                                withActions
                                className="w-36 h-36"
                            />
                        </div>
                    </div>
                    <Button className="w-fit h-fit p-2" variant="secondary" onClick={() => navigate('/orders')}>Назад</Button>
                </div>
                <div className="space-y-4 ">
                    {/* {order.productSnapshot.route.map((phase, phaseIndex) => ( */}
					{/* {order.productSnapshot.route
  					.map((phase, index) => ({ phase, index }))
  					.filter(({ phase }) =>
  					  canViewCompletion
  					    ? phase.steps.some(step =>
  					        step.assignees.some(a => a.userId === user.user.id)
  					      )
  					    : true
  					)
  					.map(({ phase, index: phaseIndex }) => ( */}

					{order.productSnapshot.route
					  .map((phase, index) => ({ phase, index })) // <— оборачиваем в объект
					  .filter(({ phase }) =>
					    canViewCompletion
					      ? phase.steps.some(step =>
					          step.assignees.some(a => a.userId === user.user.id)
					        )
					      : canViewDispatching
					      ? String(phase.phaseManager) === String(user.user.id) 
					      : true
					  )
					  .map(({ phase, index: phaseIndex }) => (


                        <div key={phaseIndex} className="border bg-[#2D2D30] border-[#3E3E42] rounded p-4 text-[#E5E5E5]">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">{phase.name}</h3>
                                <div className="flex items-center gap-4">
                                    <p className="text-gray-400">
                                        Руководитель: {phaseManagers[phaseIndex]?.lastName || 'Не назначен'} {phaseManagers[phaseIndex]?.fullName || ''}
                                    </p>
                                    <p className="text-gray-400">
                                        Отдел: {phaseDepartments[phaseIndex]?.name || 'Не указан'}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-2 py-1 rounded text-sm ${
                                            phase.status === 'done' ? 'bg-green-500' :
                                            phase.status === 'in_progress' ? 'bg-yellow-500' :
                                            'bg-gray-500'
                                        }`}>
                                            {actionLabels.status[phase.status]}
                                        </span>
                                        {canViewDispatching && phase.phaseManager === user.user.id && (
                                            <select
                                                value={phase.status}
                                                onChange={(e) => handlePhaseStatusChange(phaseIndex, e.target.value)}
                                                className="bg-[#2D2D30] border border-[#3E3E42] rounded px-2 py-1 text-sm"
                                            >
                                                <option value="pending">Ожидание</option>
                                                <option value="in_progress">В работе</option>
                                                <option value="done">Завершено</option>
                                            </select>
                                        )}
                                    </div>
                                </div>
                                {canViewOrchestration && (
                                    <AssignManagerSelect
                                        phase={phase}
                                        onAssign={(userId) => handleManagerAssign(phaseIndex, userId)}
                                    />
                                )}
                            </div>
                            <div className="mb-4">
                                <p className="text-gray-400 mb-1">Прогресс фазы:</p>
                                <ProgressBar progress={phase.progress} />
                            </div>
                            <StepList
                                steps={phase.steps}
                                departmentId={phase.departmentId}
                                phaseIndex={phaseIndex}
                                orderId={id}
                                isMaster={canViewDispatching && phase.phaseManager === user.user.id}
                                isWorker={canViewCompletion}
                                onStepComplete={(stepIndex) => handleStepComplete(phaseIndex, stepIndex)}
                                onUpdate={fetchOrder}
                            />
                        </div>
                    ))}
                </div>
            </CardAdmin>
        </div>
    );
};

export default OrderDetailPage;
